/**
 * Token Management Utilities
 * Handles token retrieval, validation, and expiration checks
 */
import md5 from 'blueimp-md5';
import { getSessionToken } from '@/utils/infrastructure/auth/sessionManager';

const PUBLIC_FRAGMENTS = ['/s/', '/external/', '/confirmAccount'];

// Milliseconds between the .NET epoch (0001-01-01) and the Unix epoch (1970-01-01).
// .NET DateTime ticks count 100ns intervals since year 1, so converting them to a
// JS-comparable Unix timestamp requires subtracting this offset.
const DOTNET_EPOCH_UNIX_OFFSET_MS = 62_135_596_800_000;

export const cSharpTicksToMilliseconds = (ticks) => {
  const seconds = ticks / 10_000_000; // 10M ticks per second
  return seconds * 1000; // 1000ms per second
};

export const isPublicFragment = () => {
  return PUBLIC_FRAGMENTS.some((fragment) => window.location.href.includes(fragment));
};

export const generateTokenKey = (global = false) => {
  if (isPublicFragment() && !global && !window.location.href.includes('authorize')) {
    const search = window.location.search;
    const code = search.split('=').pop();
    const type = search.includes('publicCode=') ? 'public' : 'external';
    const id = search.includes('publicCode=') ? code : md5(code);
    return `${type}Token"${id}"`;
  }
  return 'token';
};

export const getToken = ({ global = false } = {}) => {
  if (isPublicFragment() && !global && !window.location.href.includes('authorize')) {
    const tokenKey = generateTokenKey(global);
    const token = localStorage.getItem(`x-auth-${tokenKey}`);
    try {
      return JSON.parse(token);
    } catch (e) {
      return false;
    }
  }
  // Session-aware for automy/portal/app contexts
  return getSessionToken() || false;
};

export const isTokenExpired = ({ global = false } = {}) => {
  const token = getToken({ global });
  if (!token) return true;

  // Canonical check used across the app (UserLayout, useTokenRefresh,
  // performTokenRefresh): `issuedAt` is the client timestamp (Date.now())
  // recorded when the token was persisted, directly comparable to Date.now().
  if (token.issuedAt && token.expiresIn) {
    return Date.now() >= token.issuedAt + token.expiresIn * 1000;
  }

  // Fallback for tokens persisted without `issuedAt` (e.g. legacy or public/
  // external fragment tokens): derive the issue time from the .NET `issued`
  // ticks, converting from the .NET epoch (year 1) to the Unix epoch.
  if (token.issued && token.expiresIn) {
    const issuedUnixMs = cSharpTicksToMilliseconds(token.issued) - DOTNET_EPOCH_UNIX_OFFSET_MS;
    return Date.now() >= issuedUnixMs + token.expiresIn * 1000;
  }

  // No reliable timestamp available — treat as not expired so route guards
  // don't bounce a valid session. A real 401 will still drive refresh/logout.
  return false;
};

/**
 * Remove specific token from localStorage
 * @param {string} type - Token type (public/external)
 * @param {string} id - Token identifier
 */
export const removeTokenById = (type, id) => {
  localStorage.removeItem(`x-auth-${type}Token"${id}"`);
  localStorage.removeItem(`x-${type}-processId"${id}"`);
  localStorage.removeItem(`x-${type}-processInstance"${id}"`);
};
